import React, { useCallback, useMemo, useRef } from 'react';
import type { MouseEvent } from 'react';

import { VideoPlayerProvider } from './context';
import { VideoPlayerDraggable } from './Draggable';
import { VideoPlayerStatic } from './Static';
import { VideoPlayerCollapsed } from './Collapsed';
import { PlayerContent } from './PlayerContent';
import { VideoPlayerActionsEnum } from './utils/enum';
import { useControlsVisibility, useVideoPlayerState, useVideoSeek, useFullscreen } from './hooks';
import * as S from './styles';

const {
	TOGGLE_PLAYING_STATE,
	SET_VOLUME,
	SET_DURATION,
	SET_PLAYBACK_RATE,
	SET_IS_COLLAPSED,
	SET_PLAYED_SECONDS,
	SET_PLAYING_STATE,
	SET_MULTIPLE
} = VideoPlayerActionsEnum;

export interface VideoPlayerProps {
	children?: JSX.Element | JSX.Element[];
	src?: string;
	poster?: string;
	sound?: boolean;
	playing?: boolean;
	minimized?: boolean;
	seekTo?: number;
	showCloseButton?: boolean;
	showMinimize?: boolean;
	showVolume?: boolean;
	thumbnail?: string;
	playIconSize?: number;
	portrait?: boolean;
	actionButton?: JSX.Element;
	onActionClick?: () => void;
	onClose?: () => void;
	onMinimize?: () => void;
	onExitPIP?: () => void;
	onFullscreen?: () => void;
	onSeek?: (seconds: number) => void;
	onProgress?: (progress: { played: number; playedSeconds: number }) => void;
	progressInterval?: number;
	onDurationChange?: (duration: number) => void;
	onError?: () => void;
	playerRef?: React.MutableRefObject<VideoPlayerHandle | null>;
}

export interface VideoPlayerHandle {
	seekTo: (seconds: number) => void;
	play: () => void;
	pause: () => void;
	togglePlay: () => void;
	getCurrentTime: () => number;
	getDuration: () => number;
	expandIfCollapsed: () => void;
	expandAndSeekTo: (seconds: number) => void;
	isStaticMode: () => boolean;
	isCollapsed: () => boolean;
}

export function VideoPlayer({
	children,
	src = '',
	poster,
	sound = true,
	playing = false,
	minimized: minimizedProp,
	showCloseButton = true,
	showMinimize = true,
	showVolume = true,
	thumbnail = '',
	playIconSize,
	portrait = false,
	actionButton,
	onActionClick,
	onClose = () => {},
	onMinimize,
	onExitPIP,
	seekTo,
	onSeek,
	onProgress,
	progressInterval,
	onDurationChange,
	onError,
	playerRef: externalPlayerRef,
	onFullscreen
}: Readonly<VideoPlayerProps>) {
	const containerRef = useRef<HTMLDivElement>(null);
	const seekingRef = useRef<boolean>(false);
	const volumeRef = useRef<number>(0);
	const isDraggableRef = useRef<boolean>(false);

	const {
		isDraggable,
		isCollapsed,
		duration,
		volume,
		playbackRate,
		playedSeconds,
		playingState,
		minimized,
		seeking,
		dispatch,
		playerRef,
		previousVolumeRef
	} = useVideoPlayerState({ sound, playing, minimized: minimizedProp });

	seekingRef.current = seeking;
	volumeRef.current = volume;
	isDraggableRef.current = isDraggable;

	const { fullscreen, toggleFullscreen } = useFullscreen({ playerRef: containerRef });

	const { controlsVisible, showControls, handleVideoMouseEnter, handleVideoMouseLeave } = useControlsVisibility({
		playingState
	});

	const { handleSeek, handleReady, prepareForExpand } = useVideoSeek({
		playerRef,
		playedSeconds,
		dispatch,
		seekTo,
		onSeek,
		showControls
	});

	const handleExpandClick = useCallback(
		(event: MouseEvent): void => {
			event.stopPropagation();
			onFullscreen?.();
		},
		[onFullscreen]
	);

	const handleCloseIconClick = useCallback((): void => {
		dispatch({ type: SET_IS_COLLAPSED, payload: true });
		onClose?.();
	}, [dispatch, onClose]);

	const handleCloseClick = useCallback((): void => {
		dispatch({ type: SET_MULTIPLE, payload: { isCollapsed: true, isDraggable: false } });
		onClose?.();
	}, [dispatch, onClose]);

	const handleExpandCollapsed = useCallback((): void => {
		dispatch({
			type: SET_MULTIPLE,
			payload: { isCollapsed: false, isDraggable: true, minimized: true }
		});
		showControls();
	}, [dispatch, showControls]);

	const handleExpandIfCollapsed = useCallback((): void => {
		if (isCollapsed) {
			handleExpandCollapsed();
		}
	}, [isCollapsed, handleExpandCollapsed]);

	const handleExpandAndSeekTo = useCallback(
		(seconds: number): void => {
			prepareForExpand(seconds);
			handleExpandIfCollapsed();
		},
		[prepareForExpand, handleExpandIfCollapsed]
	);

	const handleMinimizeToStatic = useCallback((): void => {
		dispatch({ type: SET_MULTIPLE, payload: { isDraggable: false, minimized: false } });
		onExitPIP?.();
		showControls();
	}, [dispatch, onExitPIP, showControls]);

	const handleMinimizeToPIP = useCallback((): void => {
		dispatch({ type: SET_MULTIPLE, payload: { isDraggable: true, minimized: true } });
		onMinimize?.();
		showControls();
	}, [dispatch, onMinimize, showControls]);

	const handleProgress = useCallback(
		(progress: { played: number; playedSeconds: number }): void => {
			if (seekingRef.current) {
				return;
			}
			dispatch({ type: SET_PLAYED_SECONDS, payload: progress.playedSeconds });
			onProgress?.(progress);
		},
		[dispatch, onProgress]
	);

	const handleDuration = useCallback(
		(newDuration: number): void => {
			dispatch({ type: SET_DURATION, payload: newDuration });
			onDurationChange?.(newDuration);
		},
		[dispatch, onDurationChange]
	);

	const handleToggleSound = useCallback((): void => {
		if (volumeRef.current > 0) {
			previousVolumeRef.current = volumeRef.current;
			dispatch({ type: SET_VOLUME, payload: 0 });
		} else {
			dispatch({
				type: SET_VOLUME,
				payload: previousVolumeRef.current > 0 ? previousVolumeRef.current : 1
			});
		}
	}, [dispatch, previousVolumeRef]);

	const handleVolumeChange = useCallback(
		(newVolume: number): void => {
			if (!Number.isFinite(newVolume)) return;
			dispatch({ type: SET_VOLUME, payload: newVolume });
		},
		[dispatch]
	);

	const handlePlaybackRateChange = useCallback(
		(rate: number): void => {
			dispatch({ type: SET_PLAYBACK_RATE, payload: rate });
		},
		[dispatch]
	);

	const handleTogglePlay = useCallback((): void => {
		dispatch({ type: TOGGLE_PLAYING_STATE });
	}, [dispatch]);

	const handleToggleFullscreen = useCallback((): void => {
		if (isDraggableRef.current) {
			dispatch({ type: SET_MULTIPLE, payload: { isDraggable: false, minimized: false } });
			onExitPIP?.();
		}
		toggleFullscreen();
		onFullscreen?.();
	}, [dispatch, onExitPIP, toggleFullscreen, onFullscreen]);

	if (externalPlayerRef) {
		externalPlayerRef.current = {
			seekTo: handleSeek,
			play: () => dispatch({ type: SET_PLAYING_STATE, payload: true }),
			pause: () => dispatch({ type: SET_PLAYING_STATE, payload: false }),
			togglePlay: handleTogglePlay,
			getCurrentTime: () => playedSeconds,
			getDuration: () => duration,
			expandIfCollapsed: handleExpandIfCollapsed,
			expandAndSeekTo: handleExpandAndSeekTo,
			isStaticMode: () => !isCollapsed && !isDraggable,
			isCollapsed: () => isCollapsed
		};
	}

	const contextValue = useMemo(
		() => ({
			src,
			poster,
			thumbnail,
			volume,
			duration,
			playbackRate,
			playedSeconds,
			playingState,
			minimized,
			isDraggable,
			isCollapsed,
			fullscreen,
			controlsVisible,
			showCloseButton,
			showMinimize,
			showVolume,
			seeking,
			playerRef,
			previousVolumeRef,
			dispatch,
			handleSeek,
			handleReady,
			handleProgress,
			handleDuration,
			handleTogglePlay,
			handleToggleSound,
			handleVolumeChange,
			handlePlaybackRateChange,
			handleMinimizeToStatic,
			handleMinimizeToPIP,
			handleExpandCollapsed,
			handleCloseIconClick,
			handleCloseClick,
			handleExpandClick,
			handleVideoMouseEnter,
			handleVideoMouseLeave,
			handleToggleFullscreen,
			showControls
		}),
		[
			src,
			poster,
			thumbnail,
			volume,
			duration,
			playbackRate,
			playedSeconds,
			playingState,
			minimized,
			isDraggable,
			isCollapsed,
			fullscreen,
			controlsVisible,
			showCloseButton,
			showMinimize,
			showVolume,
			seeking,
			playerRef,
			previousVolumeRef,
			dispatch,
			handleSeek,
			handleReady,
			handleProgress,
			handleDuration,
			handleTogglePlay,
			handleToggleSound,
			handleVolumeChange,
			handlePlaybackRateChange,
			handleMinimizeToStatic,
			handleMinimizeToPIP,
			handleExpandCollapsed,
			handleCloseIconClick,
			handleCloseClick,
			handleExpandClick,
			handleVideoMouseEnter,
			handleVideoMouseLeave,
			handleToggleFullscreen,
			showControls
		]
	);

	if (children) {
		return <VideoPlayerProvider value={contextValue}>{children}</VideoPlayerProvider>;
	}

	function getContainer(): HTMLElement {
		return document.body;
	}

	function renderPlayerContent(): JSX.Element {
		if (isCollapsed) {
			return <VideoPlayerCollapsed />;
		}

		if (isDraggable) {
			return (
				<VideoPlayerDraggable getContainer={getContainer}>
					<PlayerContent
						src={src}
						poster={poster}
						isDraggable
						thumbnail={thumbnail}
						progressInterval={progressInterval}
					/>
				</VideoPlayerDraggable>
			);
		}

		return (
			<VideoPlayerStatic portrait={portrait}>
				<PlayerContent
					src={src}
					poster={poster}
					thumbnail={thumbnail}
					playIconSize={playIconSize}
					actionButton={actionButton}
					onActionClick={onActionClick}
					onError={onError}
					progressInterval={progressInterval}
				/>
			</VideoPlayerStatic>
		);
	}

	return (
		<VideoPlayerProvider value={contextValue}>
			<S.FullscreenContainer ref={containerRef}>{renderPlayerContent()}</S.FullscreenContainer>
		</VideoPlayerProvider>
	);
}
